import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Menu,
  InputAdornment,
  Grid,
} from "@mui/material";

import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  Mail as MailIcon,
  Search as SearchIcon,
} from "@mui/icons-material";

import { useAuthStore } from "../stores/auth.store";
import {
  UserSchema,
  AdminCreateUserRequest,
  UpdateUserRequest,
  Role,
  ASSIGNABLE_ROLES,
  getPrimaryRole,
  formatRolesLabel,
} from "../types/auth.type";

type DialogMode = "add" | "edit" | "invite";

const Users: React.FC = () => {
  const {
    getAllUsers,
    createNewUser,
    updateUser,
    removeUser,
    isLoading,
    error,
  } = useAuthStore();

  const [users, setUsers] = useState<UserSchema[]>([]);
  const [loading, setLoading] = useState(false);

  // Search and filter state
  const [searchQuery, setSearchQuery] = useState("");

  const [open, setOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<DialogMode>("add");
  const [selectedUser, setSelectedUser] = useState<UserSchema | null>(null);

  const [formData, setFormData] = useState<{
    username: string;
    email: string;
    phone: string;
    password: string;
    password_confirm: string;
    role: Role;
  }>({
    username: "",
    email: "",
    phone: "",
    password: "",
    password_confirm: "",
    role: Role.USER,
  });

  // Actions menu state
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuUser, setMenuUser] = useState<UserSchema | null>(null);

  const getUserId = (u: any) => u?.id || u?.sub || "";

  /* ------------------------- Date Formatting Helpers ----------------------- */

  const formatDate = (dateString?: string) => {
    if (!dateString) return "N/A";
    try {
      // Ensure date is parsed as UTC - if no timezone info, treat as UTC
      let dateStr = dateString.trim();
      if (!dateStr.endsWith("Z") && !dateStr.includes("+") && !dateStr.includes("-", 10)) {
        // If no timezone info, assume UTC and append 'Z'
        dateStr = dateStr.replace(" ", "T") + "Z";
      } else if (dateStr.includes(" ") && !dateStr.includes("T")) {
        // Replace space with T for ISO format
        dateStr = dateStr.replace(" ", "T");
      }
      
      const date = new Date(dateStr);
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return dateString;
      }
      
      // Get UTC components and add 3 hours manually to avoid double conversion
      let utcHour = date.getUTCHours() + 3;
      let utcDay = date.getUTCDate();
      let utcMonth = date.getUTCMonth();
      let utcYear = date.getUTCFullYear();
      
      // Handle hour overflow
      if (utcHour >= 24) {
        utcHour -= 24;
        utcDay += 1;
        // Handle day overflow (simplified - works for most cases)
        const daysInMonth = new Date(utcYear, utcMonth + 1, 0).getDate();
        if (utcDay > daysInMonth) {
          utcDay = 1;
          utcMonth += 1;
          if (utcMonth >= 12) {
            utcMonth = 0;
            utcYear += 1;
          }
        }
      }
      
      // Format the date
      const year = utcYear;
      const month = String(utcMonth + 1).padStart(2, "0");
      const day = String(utcDay).padStart(2, "0");
      const hour = String(utcHour).padStart(2, "0");
      const minute = String(date.getUTCMinutes()).padStart(2, "0");
      const second = String(date.getUTCSeconds()).padStart(2, "0");
      
      return `${month}/${day}/${year}, ${hour}:${minute}:${second}`;
    } catch {
      return dateString;
    }
  };

  const formatRelativeTime = (dateString?: string) => {
    if (!dateString) return "N/A";
    try {
      // Ensure date is parsed as UTC - same logic as formatDate
      let dateStr = dateString.trim();
      if (!dateStr.endsWith("Z") && !dateStr.includes("+") && !dateStr.includes("-", 10)) {
        // If no timezone info, assume UTC and append 'Z'
        dateStr = dateStr.replace(" ", "T") + "Z";
      } else if (dateStr.includes(" ") && !dateStr.includes("T")) {
        // Replace space with T for ISO format
        dateStr = dateStr.replace(" ", "T");
      }
      
      const date = new Date(dateStr);
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return "N/A";
      }
      
      // Add 3 hours to convert UTC to UTC+3
      const utc3Time = date.getTime() + 3 * 60 * 60 * 1000;
      
      // Get current UTC time and add 3 hours for comparison
      const now = new Date();
      const nowUtc3Time = now.getTime() + 3 * 60 * 60 * 1000;
      
      // Calculate difference (time difference is timezone-independent)
      const diffMs = nowUtc3Time - utc3Time;
      const diffSeconds = Math.floor(diffMs / 1000);
      const diffMinutes = Math.floor(diffSeconds / 60);
      const diffHours = Math.floor(diffMinutes / 60);
      const diffDays = Math.floor(diffHours / 24);
      const diffMonths = Math.floor(diffDays / 30);
      const diffYears = Math.floor(diffDays / 365);

      if (diffSeconds < 60) {
        return "just now";
      } else if (diffMinutes < 60) {
        return `${diffMinutes} ${diffMinutes === 1 ? "minute" : "minutes"} ago`;
      } else if (diffHours < 24) {
        return `${diffHours} ${diffHours === 1 ? "hour" : "hours"} ago`;
      } else if (diffDays < 30) {
        return `${diffDays} ${diffDays === 1 ? "day" : "days"} ago`;
      } else if (diffMonths < 12) {
        return `${diffMonths} ${diffMonths === 1 ? "month" : "months"} ago`;
      } else {
        return `${diffYears} ${diffYears === 1 ? "year" : "years"} ago`;
      }
    } catch {
      return "N/A";
    }
  };

  /* --------------------------------- Loaders -------------------------------- */

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const allUsers = await getAllUsers();
      // Normalize users to ensure email is at top level
      const normalizedUsers = (allUsers || []).map((u: any) => ({
        ...u,
        email: u.email || "",
        username: u.username || "",
        id: u.id || u.sub || "",
      }));
      setUsers(normalizedUsers);
    } catch (err) {
      console.error("Error fetching users", err);
    } finally {
      setLoading(false);
    }
  };

  /* -------------------------- Filtered Users -------------------------- */

  const filteredUsers = useMemo(() => {
    let result = [...users];

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (u) =>
          (u.username || "").toLowerCase().includes(query) ||
          (u.email || "").toLowerCase().includes(query)
      );
    }

    return result;
  }, [users, searchQuery]);

  /* ------------------------------ Add / Edit / Invite ------------------------------- */

  const handleOpen = (mode: DialogMode, user?: UserSchema) => {
    setDialogMode(mode);

    if (mode === "edit" && user) {
      setSelectedUser(user);
      setFormData({
        username: user.username || "",
        email: user.email || String(user.data?.email ?? "") || "",
        phone: user.phone || "",
        password: "",
        password_confirm: "",
        role: getPrimaryRole(user.roles),
      });
    } else {
      setSelectedUser(null);
      setFormData({
        username: "",
        email: "",
        phone: "",
        password: "",
        password_confirm: "",
        role: Role.USER,
      });
    }
    setOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.username.trim()) {
      alert("Please enter a username");
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      alert("Please enter a valid email address");
      return;
    }

    if (dialogMode === "invite") {
      // TODO: Implement invite user logic
      alert(`Invite functionality will send an email to ${formData.email}`);
      console.log("Invite user:", {
        username: formData.username,
        email: formData.email,
      });
      setOpen(false);
      return;
    }

    if (dialogMode === "add") {
      if (formData.password !== formData.password_confirm) {
        alert("Passwords do not match");
        return;
      }

      const createBody: AdminCreateUserRequest = {
        username: formData.username,
        email: formData.email,
        phone: formData.phone || "",
        password: formData.password,
        password_confirm: formData.password_confirm,
        roles: [formData.role],
      };

      if (formData.phone.trim()) {
        createBody.phone = formData.phone.trim();
      }

      await createNewUser(createBody);
    } else if (dialogMode === "edit") {
      const updateBody: UpdateUserRequest = {
        username: formData.username,
        email: formData.email,
        phone: formData.phone || null,
        roles: [formData.role],
        data: {},
      };

      const userId = getUserId(selectedUser!);
      await updateUser(userId, updateBody);
    }

    setOpen(false);
    loadUsers();
  };

  /* ------------------------------ Actions Menu -------------------------- */

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    user: UserSchema
  ) => {
    setAnchorEl(event.currentTarget);
    setMenuUser(user);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuUser(null);
  };

  const handleEditUser = () => {
    if (menuUser) {
      handleOpen("edit", menuUser);
    }
    handleMenuClose();
  };

  const handleDeleteUser = async () => {
    if (!menuUser) return;
    const uid = getUserId(menuUser);

    if (window.confirm("Are you sure you want to delete this user?")) {
      await removeUser(uid);
      loadUsers();
    }
    handleMenuClose();
  };

  /* --------------------------------- Render -------------------------------- */

  if ((loading || isLoading) && users.length === 0) {
    return (
      <Box display="flex" justifyContent="center" mt={6}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" mb={3}>
        <Typography variant="h4">Users</Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<MailIcon />}
            onClick={() => handleOpen("invite")}
          >
            Invite User
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpen("add")}
          >
            Add User
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Search and Filter Section */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by username or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>

        {searchQuery && (
          <Box mt={2}>
            <Typography variant="body2" color="text.secondary">
              Showing {filteredUsers.length} of {users.length} users
              {searchQuery && ` matching "${searchQuery}"`}
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Users Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <strong>Username</strong>
              </TableCell>
              <TableCell>
                <strong>Email</strong>
              </TableCell>
              <TableCell>
                <strong>Phone</strong>
              </TableCell>
              <TableCell>
                <strong>Roles</strong>
              </TableCell>
              <TableCell>
                <strong>Created At</strong>
              </TableCell>
              <TableCell>
                <strong>Updated At</strong>
              </TableCell>
              <TableCell align="right">
                <strong>Actions</strong>
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {filteredUsers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ py: 3 }}
                  >
                    {searchQuery
                      ? "No users found matching your search"
                      : "No users found"}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredUsers.map((u) => {
                const uid = getUserId(u);

                return (
                  <TableRow key={uid} hover>
                    <TableCell>{u.username}</TableCell>
                    <TableCell>
                      {u.email || String(u.data?.email ?? "") || "N/A"}
                    </TableCell>
                    <TableCell>{u.phone || "N/A"}</TableCell>
                    <TableCell>{formatRolesLabel(u.roles)}</TableCell>
                    <TableCell>{formatDate(u.created_at)}</TableCell>
                    <TableCell>{formatDate(u.updated_at)}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, u)}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleEditUser}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          Edit User
        </MenuItem>
        <MenuItem onClick={handleDeleteUser} sx={{ color: "error.main" }}>
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Delete User
        </MenuItem>
      </Menu>

      {/* Add / Edit / Invite User Dialog */}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {dialogMode === "add" && "Add User"}
          {dialogMode === "edit" && "Edit User"}
          {dialogMode === "invite" && "Invite User"}
        </DialogTitle>

        <form onSubmit={handleSubmit}>
          <DialogContent>
            <TextField
              fullWidth
              label="Username"
              required
              margin="normal"
              value={formData.username}
              onChange={(e) =>
                setFormData((p) => ({ ...p, username: e.target.value }))
              }
              helperText="Enter a unique username"
            />

            <TextField
              fullWidth
              label="Email"
              type="email"
              required
              margin="normal"
              value={formData.email}
              onChange={(e) =>
                setFormData((p) => ({ ...p, email: e.target.value }))
              }
              helperText="Must be a valid email address"
            />

            {(dialogMode === "add" || dialogMode === "edit") && (
              <TextField
                fullWidth
                label="Phone"
                margin="normal"
                value={formData.phone}
                onChange={(e) =>
                  setFormData((p) => ({ ...p, phone: e.target.value }))
                }
                helperText={
                  dialogMode === "add"
                    ? "Optional — omit to avoid linking a phone number"
                    : undefined
                }
              />
            )}

            {(dialogMode === "add" || dialogMode === "edit") && (
              <TextField
                fullWidth
                select
                label="Role"
                margin="normal"
                value={formData.role}
                onChange={(e) =>
                  setFormData((p) => ({ ...p, role: e.target.value as Role }))
                }
                helperText={
                  dialogMode === "add"
                    ? "Created without OTP SMS"
                    : "User must log in again for role changes to apply"
                }
              >
                {ASSIGNABLE_ROLES.map((role) => (
                  <MenuItem key={role} value={role}>
                    {role}
                  </MenuItem>
                ))}
              </TextField>
            )}

            {dialogMode === "add" && (
              <>
                <TextField
                  fullWidth
                  label="Password"
                  type="password"
                  margin="normal"
                  required
                  value={formData.password}
                  onChange={(e) =>
                    setFormData((p) => ({ ...p, password: e.target.value }))
                  }
                />
                <TextField
                  fullWidth
                  label="Confirm Password"
                  type="password"
                  margin="normal"
                  required
                  value={formData.password_confirm}
                  onChange={(e) =>
                    setFormData((p) => ({
                      ...p,
                      password_confirm: e.target.value,
                    }))
                  }
                />
              </>
            )}

            {dialogMode === "invite" && (
              <Alert severity="info" sx={{ mt: 2 }}>
                An invitation email will be sent to the user's email address
                with instructions to set up their account.
              </Alert>
            )}
          </DialogContent>

          <DialogActions>
            <Button onClick={() => setOpen(false)}>Cancel</Button>
            <Button
              type="submit"
              variant="contained"
            >
              {dialogMode === "add" && "Create"}
              {dialogMode === "edit" && "Update"}
              {dialogMode === "invite" && "Send Invite"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default Users;
